import { PhoneNumberTypes } from 'Constants/enums';
import { MobXProviderContext } from 'mobx-react';
import { ContactModelV2 } from 'Models/ContactModelV2';
import { useContext, useEffect } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { ContactInfoResult } from '../../types';

/**
 * Hook to get the contact info for a call log item. It will get the person and contacts reactively from MobX store and return the name and phone number to display, as well as if the person is inactive.
 * @param personId - The person id
 * @param phoneNumber - The phone number
 * @param fullName - The full name
 * @param viaName - The via name
 * @param overrideContact - When provided, overrides the resolved name, avatar and presence with the selected contact's identity.
 * @returns The name and phone number to display, as well as if the person is inactive.
 */
export const useContactInfo = (
  personId: number,
  phoneNumber: string,
  fullName: string,
  viaName: string,
  overrideContact?: ContactModelV2
): ContactInfoResult => {
  const {
    person: {
      selectPersonValueById,
      loadPersonByIdGetIfMissingGet,
      getAllContactsWithSameNum,
    },
    ui: { selectPersonPresenceStatus },
  } = useContext<RootStoreProps>(MobXProviderContext);

  // Load person if it exists
  useEffect(() => {
    if (personId) {
      void loadPersonByIdGetIfMissingGet(personId.toString());
      return;
    }
  }, [loadPersonByIdGetIfMissingGet, personId]);

  // Get person and contacts reactively from MobX store
  let nameToDisplay = '';
  let phoneNumberToDisplay = '';
  let isPersonInactive = false;
  let avatarToDisplay = '';
  let isUnsavedContact = false;
  let isInternal = false;
  let matchedPersonId: number | null = null;

  // If has personId, get person from MobX store
  if (personId) {
    const person = selectPersonValueById(personId);
    nameToDisplay =
      person?.name ||
      fullName ||
      viaName ||
      ContactModelV2.formatNumber(phoneNumber);
    isPersonInactive = person?.deleted || false;
    avatarToDisplay = person?.avatarUrl || '';
    isInternal = person?.source === 'Bhive';

    // If there's also a phone number, check if there's an external contact match
    // External contacts should not show status even if personId matches an internal user
    if (phoneNumber && isInternal) {
      const contacts = getAllContactsWithSameNum(phoneNumber);
      const hasExternalContact =
        contacts?.state === 'fulfilled' &&
        contacts.value.length > 0 &&
        contacts.value[0]?.source !== 'Bhive';

      if (hasExternalContact) {
        isInternal = false;
      }
    }

    // Store the matched personId for presence lookup
    if (isInternal) {
      matchedPersonId = personId;
    }
  } else {
    // If no personId, get contacts with the given phone number from MobX store
    const contacts = getAllContactsWithSameNum(phoneNumber);

    // If contacts are fulfilled, get the first contact (priority contact)
    if (contacts?.state === 'fulfilled') {
      const contact = contacts.value[0];

      // Get the matching phone number from the contact.
      // We have to do this because we have to display the phone number type in the UI, and we don't get this information from the call logs API.
      const matchingPhoneNumber = contact?.getMatchingPhoneNumber(phoneNumber);

      // If contact exists, use the contact and matching phone number to set the name and phone number to display
      if (contact) {
        nameToDisplay = contact.name;

        phoneNumberToDisplay = matchingPhoneNumber
          ? `${ContactModelV2.formatNumber(matchingPhoneNumber.number)} [${PhoneNumberTypes.find(
            (type) => type.key === matchingPhoneNumber.type
          )?.text
          }]`
          : phoneNumber;

        avatarToDisplay = contact.avatarUrl || '';
        isInternal = contact.source === 'Bhive';

        // Store the matched personId for presence lookup
        if (isInternal && contact.sourceId) {
          matchedPersonId = parseInt(contact.sourceId);
        }
      } else {
        // If no contact exists, use the given phone number to set the name to display
        nameToDisplay =
          ContactModelV2.formatNumber(phoneNumber) || fullName || viaName;
        isUnsavedContact = true;
        isInternal = false;
      }
    } else {
      // If no contacts exist, use the given phone number, full name or via nameto set the name to display
      nameToDisplay =
        ContactModelV2.formatNumber(phoneNumber) || fullName || viaName;
      isUnsavedContact = true;
      isInternal = false;
    }
  }

  // Get presence status only for internal contacts using the matched personId
  const presence =
    isInternal && matchedPersonId
      ? selectPersonPresenceStatus(matchedPersonId.toString())
      : null;

  // When a saved contact search is active, overrideContact is set to ensure all
  // results consistently display the selected contact's identity, since searching
  // by phone numbers may return logs from other contacts sharing the same number.
  if (overrideContact) {
    const overrideIsInternal = overrideContact.source === 'Bhive';
    const overrideSourceId = overrideContact.sourceId;
    const overrideName = overrideContact.name ?? nameToDisplay;
    const overrideAvatar = overrideContact.avatarUrl ?? '';
    const overridePresence = overrideIsInternal && overrideSourceId
      ? selectPersonPresenceStatus(overrideSourceId)
      : null;

    return {
      nameToDisplay: overrideName,
      avatarToDisplay: overrideAvatar,
      phoneNumberToDisplay,
      isPersonInactive,
      isUnsavedContact,
      presence: overridePresence,
    };
  }

  return {
    nameToDisplay,
    phoneNumberToDisplay,
    isPersonInactive,
    avatarToDisplay,
    isUnsavedContact,
    presence,
  };
};
